import { FixLogProps, LogFetchParam, LogSearchParam, QuickSearchParam, SendLogParam } from '@@types/Api/Log';
import { CHANNEL_ID, ContentsWrap, LOG_ID } from '@@types/common';
import Log, { AllSearchLog, ForwardLog, ReplyDummyLog, ReplyLog } from '@@types/Log';
import LogsResponse, { OldLogsResponse } from '@@types/Log/Response';
import * as Sentry from '@sentry/react';

import { URLS } from '~/constants';
import { LOG_SEND_TIMEOUT } from '~/constants/api';
import { mainDetailUiState } from '~/stores/DetailUiState';
import Api2 from '~/stores/providers/Api2';
import { isMainView } from '~/utils/runEnvironmentUtil';
import { curry, omit } from 'ramda';

import * as packageJson from '../../../../package.json';

class LogApi {
  forwardLog = curry((targetChannelId: CHANNEL_ID, channelTitle: string, log: Log): Promise<ForwardLog> => {
    return this.api.contentsRequester.post(
      `${URLS.API}/channels/${log.channelId}/logs/${log.id}/forward`,
      { channelTitle, log },
      { params: { to: targetChannelId } }
    );
  });

  constructor(private api: Api2) {}

  fetchLogs = ({ channelId, ...params }: LogFetchParam): Promise<LogsResponse> => {
    // @ts-ignore version 정보 테스트로 추가
    params.v = packageJson.version;

    if (!channelId) {
      Sentry.captureException(new Error('No channelId'), {
        level: 'fatal',
        extra: {
          channelId,
          ...params,
          mainDetailUiState,
        },
      });
    }

    return (
      this.api.resultRequester.cancelGet(
        `${URLS.API}/channel/${channelId}/logs`,
        `${channelId}${JSON.stringify(params)}`,
        {
          params: { limit: 50, setLastChannel: isMainView(), ...params },
        }
      ) as Promise<OldLogsResponse>
    ).then((result) => {
      return {
        result: result.content,
        references: result.references,
      };
    });
  };

  sendLog = ({ channelId, text, token }: SendLogParam): Promise<Log> => {
    return this.api.contentsRequester.post(
      `${URLS.API}/channelLogs/${channelId}`,
      { text, token },
      { timeout: LOG_SEND_TIMEOUT }
    );
  };

  deleteLog = (channelId: CHANNEL_ID, logId: LOG_ID) => {
    return this.api.contentsRequester.delete(`${URLS.API}/channels/${channelId}/logs/${logId}`);
  };

  deleteLogs = (channelId: CHANNEL_ID, logs: Array<{ logId: LOG_ID; channelId: CHANNEL_ID }>) => {
    return this.api.resultRequester.post(`${URLS.API}/channels/${channelId}/delete-logs`, logs);
  };

  updateLog = (channelId: CHANNEL_ID, logId: LOG_ID, text: string) => {
    return this.api.contentsRequester.put(`${URLS.API}/channels/${channelId}/logs/${logId}`, { text });
  };

  replyLog = (
    targetLog: { id: string },
    { text, token, channelId }: ReplyDummyLog & FixLogProps
  ): Promise<ReplyLog> => {
    return this.api.contentsRequester.post(
      `${URLS.API}/channels/${channelId}/logs/${targetLog.id}/reply`,
      { text, token },
      { timeout: LOG_SEND_TIMEOUT }
    );
  };

  searchLogs = (params: LogSearchParam): Promise<ContentsWrap<AllSearchLog>> => {
    return this.api.resultRequester.get(`${URLS.API}/search`, {
      params: omit(['channelId'], params),
    });
  };

  searchLogsInChannel = ({ channelId, ...params }: LogSearchParam): Promise<ContentsWrap<AllSearchLog>> => {
    return this.api.resultRequester.cancelGet(`${URLS.API}/channels/${channelId}/search`, channelId, {
      params,
    });
  };

  searchQuickSearch = (params: QuickSearchParam): Promise<ContentsWrap<Log[]>> => {
    return this.api.resultRequester.get(`${URLS.API}/quick-search`, { params });
  };

  searchQuickSearchInChannel = ({ channelId, ...params }: QuickSearchParam): Promise<ContentsWrap<Log[]>> => {
    return this.api.resultRequester.cancelGet(`${URLS.API}/channels/${channelId}/quick-search`, channelId, {
      params,
    });
  };

  attachReaction = ({ channelId, logId, emoticon }: { channelId: CHANNEL_ID; logId: LOG_ID; emoticon: string }) => {
    return this.api.resultRequester.post(`${URLS.API}/channels/${channelId}/logs/${logId}/reactions`, {
      value: emoticon,
    });
  };

  detachReaction = ({ channelId, logId, reactionId }: { channelId: CHANNEL_ID; logId: LOG_ID; reactionId: string }) => {
    return this.api.resultRequester.delete(`${URLS.API}/channels/${channelId}/logs/${logId}/reactions/${reactionId}`);
  };
}

export default LogApi;
